import { useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from '../store/hooks'
import { downloadJobs } from '../store/downloadJobs/slice'

const POLL_INTERVAL = 4000
// After this many consecutive status-check failures the job is treated as
// unrecoverable (e.g. the server-side record expired after its TTL, common
// when the tab was reloaded long after the job started) so the poller stops
// instead of looping forever.
const MAX_POLL_FAILURES = 5

interface JobStatusResponse {
  job_id?: string
  status: 'pending' | 'processing' | 'uploading' | 'completed' | 'failed'
  progress: number
  download_url: string | null
  file_name: string | null
  error: string | null
  total_expenses: number | null
  processed_expenses: number | null
  current_phase: string | null
}

type GetStatusFn = (jobId: string) => Promise<JobStatusResponse>

// Persisted across effect re-runs and component remounts (incl. React
// StrictMode's double-invoke) so a given job's file is triggered exactly once.
// jobIds are unique per download, so a real re-download gets a fresh id.
const triggeredDownloads = new Set<string>()

const triggerBrowserDownload = (url: string, fileName: string | null): void => {
  const a = document.createElement('a')
  a.href = url
  a.download = fileName || 'download'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

export const useJobPoller = (jobId: string | null, getStatusFn: GetStatusFn, onComplete?: () => void, onError?: (error: string) => void) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const onCompleteRef = useRef(onComplete)
  const onErrorRef = useRef(onError)
  const finalizedRef = useRef(false)
  const failuresRef = useRef(0)
  const tRef = useRef(t)

  onCompleteRef.current = onComplete
  onErrorRef.current = onError
  tRef.current = t

  const stopPolling = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!jobId) return

    finalizedRef.current = false
    failuresRef.current = 0

    const finalizeWithError = (message: string, removeAfterMs: number) => {
      finalizedRef.current = true
      stopPolling()
      onErrorRef.current?.(message)
      setTimeout(() => dispatch(downloadJobs.actions.removeJob(jobId)), removeAfterMs)
    }

    const poll = async () => {
      if (finalizedRef.current) return

      try {
        const data = await getStatusFn(jobId)

        if (finalizedRef.current) return

        failuresRef.current = 0

        dispatch(
          downloadJobs.actions.updateJob({
            jobId,
            status: data.status,
            progress: data.progress,
            downloadUrl: data.download_url,
            fileName: data.file_name,
            error: data.error,
            totalExpenses: data.total_expenses ?? null,
            processedExpenses: data.processed_expenses ?? null,
            currentPhase: data.current_phase ?? null
          })
        )

        if (data.status === 'completed') {
          finalizedRef.current = true
          stopPolling()
          if (data.download_url) {
            // Idempotent across concurrent/duplicate poll loops: only the first
            // one for this jobId triggers the download and the completion toast.
            if (!triggeredDownloads.has(jobId)) {
              triggeredDownloads.add(jobId)
              triggerBrowserDownload(data.download_url, data.file_name)
              onCompleteRef.current?.()
            }
            setTimeout(() => {
              triggeredDownloads.delete(jobId)
              dispatch(downloadJobs.actions.removeJob(jobId))
            }, 5000)
          } else {
            // Job reported completed but produced no link — surface an error
            // instead of polling forever on a terminal state.
            onErrorRef.current?.(tRef.current('download.useJobPoller.linkFailed'))
            setTimeout(() => dispatch(downloadJobs.actions.removeJob(jobId)), 8000)
          }
          return
        }

        if (data.status === 'failed') {
          finalizedRef.current = true
          stopPolling()
          onErrorRef.current?.(data.error || tRef.current('conciliation.errors.unknown'))
          setTimeout(() => dispatch(downloadJobs.actions.removeJob(jobId)), 8000)
          return
        }
      } catch (err) {
        failuresRef.current += 1
        const errorStatus = (err as { response?: { status?: number }; status?: number })?.response?.status
        const notFound = errorStatus === 404
        if (notFound || failuresRef.current >= MAX_POLL_FAILURES) {
          finalizeWithError(tRef.current('download.useJobPoller.expired'), 6000)
          return
        }
        // Other transient errors are non-fatal — keep polling.
      }

      // Schedule next poll only after current one completes
      timeoutRef.current = setTimeout(poll, POLL_INTERVAL)
    }

    poll()

    return stopPolling
  }, [jobId, dispatch, stopPolling, getStatusFn])

  return { stopPolling }
}
